import { useMemo } from 'react';
import { useSearchParams } from '@remix-run/react';

import type { SearchServiceViewProjectsByOption } from '../type/api';
import { PAGE_KEY } from '~/modules/pagination/config/url-page-keys';
import { useMatchesHandleData } from '~/modules/root';
import type { ConstantLengthArray } from '~/utils/types';

export const VIEW_PROJECTS_BY_KEY = 'viewProjectsBy';

export const VIEW_PROJECTS_BY_OPTIONS: ConstantLengthArray<
    {
        item: string;
        param: SearchServiceViewProjectsByOption;
    },
    3
> = [
    {
        item: 'All',
        param: 'all',
    },
    {
        item: 'Our Projects',
        param: 'our-projects',
    },
    {
        item: "Projects We're Tagged In",
        param: 'projects-we-tagged-in',
    },
];

export interface ViewProjectsByOption {
    name: string;
    value: SearchServiceViewProjectsByOption;
    selected: boolean;
    header: string;
}

/**
 * Hook to get the options for the `viewProjectsBy` filter.
 */
export function useViewProjectsBy(): {
    viewOptions: ViewProjectsByOption[];
    defaultOptionName: string | undefined;
} {
    const showViewProjectsByFilters = useMatchesHandleData(
        'showViewProjectsByFilters',
        false
    );
    const [searchParams] = useSearchParams();

    return useMemo(() => {
        if (!showViewProjectsByFilters) {
            return { viewOptions: [], defaultOptionName: '' };
        }

        const availableOptions = VIEW_PROJECTS_BY_OPTIONS;

        const urlSortString = searchParams.get(VIEW_PROJECTS_BY_KEY);
        const urlSort = validateViewProjectsBy(urlSortString);

        let selectedOptionIndex = availableOptions.findIndex(
            (o) => o.param === urlSort
        );
        if (selectedOptionIndex === -1) selectedOptionIndex = 0;

        const defaultOptionName = availableOptions[0]?.item;

        const viewOptions = availableOptions.map((o, index) => {
            const copyParams = new URLSearchParams(searchParams);
            if (o.param) {
                copyParams.set(VIEW_PROJECTS_BY_KEY, o.param.toString());
            } else {
                copyParams.delete(VIEW_PROJECTS_BY_KEY);
            }
            copyParams.delete(PAGE_KEY);
            return {
                name: o.item,
                value: o.param,
                selected: selectedOptionIndex === index,
                header: o.item,
                state: { scroll: false },
            };
        });

        return {
            viewOptions,
            defaultOptionName,
        };
    }, [searchParams, showViewProjectsByFilters]);
}

/**
 * Check if a string is a valid option for the `viewProjectsBy` filter.
 * @param value The string to check.
 * @returns The value if it is a valid option, `null` otherwise.
 */
export function validateViewProjectsBy(
    value?: string | null
): SearchServiceViewProjectsByOption | null {
    if (!value) {
        return null;
    }
    for (const option of VIEW_PROJECTS_BY_OPTIONS) {
        if (option.param === value) {
            return value;
        }
    }
    return null;
}
